/**
 * Async display-timeline precompute: worker for large computed series, sync fallback otherwise.
 */

import { debug, error as logError } from "./console";
import {
  DEFAULT_TIMESERIES_LINE_TYPE,
  normalizeLineType,
  precomputeSeriesDisplayTimeline,
} from "./timeseriesSeriesTransforms";
import type { DisplayTimelineWorkerResponse } from "../workers/timeseries-display-timeline-processor";

/** Computed line types at or above this point count run in a worker (keeps fetch UI responsive). */
export const DISPLAY_TIMELINE_PRECOMPUTE_WORKER_MIN_POINTS = 5_000;

type SeriesPointInput = {
  x: Date | unknown;
  y: number | null | undefined;
  displayY?: number | null;
};

function pointTimeMs(x: unknown): number {
  if (x instanceof Date) {return x.getTime();}
  const t = new Date(x as string | number).getTime();
  return Number.isFinite(t) ? t : Number.NaN;
}

function shouldUseDisplayTimelineWorker(
  sortedData: SeriesPointInput[],
  lineTypeRaw: unknown
): boolean {
  if (typeof Worker === "undefined") {return false;}
  const lineType = normalizeLineType(lineTypeRaw);
  if (lineType === DEFAULT_TIMESERIES_LINE_TYPE) {return false;}
  return sortedData.length >= DISPLAY_TIMELINE_PRECOMPUTE_WORKER_MIN_POINTS;
}

async function runDisplayTimelineWorker(
  points: Array<{ xMs: number; y: number }>,
  lineTypeRaw: unknown,
  segmentGapMs: number
): Promise<Array<number | null>> {
  const { default: DisplayTimelineWorker } = await import(
    "../workers/timeseries-display-timeline-processor.ts?worker"
  );
  const worker = new DisplayTimelineWorker();

  return new Promise((resolve, reject) => {
    const messageId = `display-timeline-${Date.now()}-${Math.random()}`;
    const startTime = performance.now();
    let settled = false;

    const finish = (fn: () => void) => {
      if (settled) {return;}
      settled = true;
      clearTimeout(timeoutId);
      worker.removeEventListener("message", handleMessage);
      worker.removeEventListener("error", handleError);
      worker.terminate();
      fn();
    };

    const handleMessage = (event: MessageEvent<DisplayTimelineWorkerResponse>) => {
      if (event.data.id !== messageId || event.data.type !== "DISPLAY_TIMELINE_PRECOMPUTED") {
        return;
      }
      if (event.data.success && event.data.displayYs) {
        debug("[DisplayTimelinePrecompute] worker completed", {
          points: points.length,
          ms: Math.round(performance.now() - startTime),
        });
        finish(() => resolve(event.data.displayYs!));
      } else {
        finish(() => reject(new Error(event.data.error || "Display timeline worker failed")));
      }
    };

    const handleError = (error: ErrorEvent) => {
      finish(() => reject(new Error(error.message || "Display timeline worker error")));
    };

    worker.addEventListener("message", handleMessage);
    worker.addEventListener("error", handleError);

    const baseTimeout = 30_000;
    const sizeBonus = Math.min(120_000, Math.floor(points.length / 1000) * 1000);
    const timeoutMs = baseTimeout + sizeBonus;

    const timeoutId = setTimeout(() => {
      finish(() => reject(new Error(`Display timeline worker timeout after ${timeoutMs}ms`)));
    }, timeoutMs);

    worker.postMessage({
      id: messageId,
      type: "PRECOMPUTE_DISPLAY_TIMELINE",
      points,
      lineType: lineTypeRaw,
      segmentGapMs,
    });
  });
}

/**
 * Precompute `displayY` on the main thread or in a worker depending on size and line type.
 */
export async function precomputeSeriesDisplayTimelineAsync<T extends Record<string, unknown>>(
  sortedData: Array<T & SeriesPointInput>,
  lineTypeRaw: unknown,
  segmentGapMs: number
): Promise<Array<T & { x: Date; y: number; displayY: number | null }>> {
  if (!shouldUseDisplayTimelineWorker(sortedData, lineTypeRaw)) {
    return precomputeSeriesDisplayTimeline(sortedData, lineTypeRaw, segmentGapMs);
  }

  const workerPoints = sortedData.map((p) => {
    const xMs = pointTimeMs(p.x);
    const yNum = Number(p.y);
    return { xMs, y: Number.isFinite(yNum) ? yNum : Number.NaN };
  });

  try {
    const displayYs = await runDisplayTimelineWorker(workerPoints, lineTypeRaw, segmentGapMs);
    return sortedData.map((p, i) => {
      const x = p.x instanceof Date ? p.x : new Date(p.x as string | number);
      const yNum = Number(p.y);
      return {
        ...p,
        x,
        y: Number.isFinite(yNum) ? yNum : (p.y as number),
        displayY: displayYs[i] ?? null,
      };
    });
  } catch (error) {
    logError("[DisplayTimelinePrecompute] Worker failed; falling back to main thread", error);
    return precomputeSeriesDisplayTimeline(sortedData, lineTypeRaw, segmentGapMs);
  }
}
